import * as React from "react";
import { BatchedSaveQueue } from "../utils/decisiontree/save-queue";

export type SaveStatus = "idle" | "saving" | "saved" | "error" | "stale";
export type SaveType = "drag" | "layout" | "edit" | null;

/**
 * Hook for managing save status display with queue awareness.
 *
 * Uses an active save counter to track concurrent operations.
 * Only shows "saved" when ALL work is truly done (no active saves,
 * no pending collects, no queued operations).
 */
export function useSaveStatus(saveQueue: React.RefObject<BatchedSaveQueue<any>>) {
    const activeSaveCountRef = React.useRef(0);
    const [saveStatus, setSaveStatus] = React.useState<SaveStatus>("idle");
    const [saveType, setSaveType] = React.useState<SaveType>(null);
    const savedTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    /** Call when a save operation starts (1:1 with markSaveCompleted/markSaveFailed) */
    const markSaveStarted = React.useCallback(() => {
        activeSaveCountRef.current++;
        if (savedTimerRef.current) {
            clearTimeout(savedTimerRef.current);
            savedTimerRef.current = null;
        }
        setSaveStatus("saving");
    }, []);

    /** Call when a save operation completes successfully */
    const markSaveCompleted = React.useCallback(() => {
        activeSaveCountRef.current = Math.max(0, activeSaveCountRef.current - 1);
        // Only show "saved" when ALL work is truly done.
        // Note: we intentionally do NOT check isProcessingQueue() here.
        // markSaveCompleted is called from INSIDE queue operations, where
        // isProcessing is always true — checking it would deadlock.
        // hasUnsavedChanges() checks it because it's called externally.
        if (activeSaveCountRef.current === 0 &&
            !saveQueue.current?.hasPending() &&
            (saveQueue.current?.getQueueLength() ?? 0) === 0) {
            setSaveStatus("saved");
            setSaveType(null);
            savedTimerRef.current = setTimeout(() => {
                setSaveStatus("idle");
                savedTimerRef.current = null;
            }, 2000);
        }
    }, [saveQueue]);

    /**
     * Call when save succeeded but view refresh failed.
     * Shows "stale" status instead of misleading "error" — the edit IS saved on server,
     * but the UI may not reflect latest changes until a page refresh.
     */
    const markSaveCompletedWithStaleView = React.useCallback(() => {
        activeSaveCountRef.current = Math.max(0, activeSaveCountRef.current - 1);
        setSaveStatus("stale");
        setSaveType(null);
        // Auto-dismiss after 5s — stale is informational, not actionable.
        // User's edit IS saved; this just means the view may not reflect it.
        savedTimerRef.current = setTimeout(() => {
            setSaveStatus("idle");
            savedTimerRef.current = null;
        }, 5000);
    }, []);

    /** Call when a save operation fails */
    const markSaveFailed = React.useCallback(() => {
        activeSaveCountRef.current = Math.max(0, activeSaveCountRef.current - 1);
        setSaveStatus("error");
    }, []);

    /** Check if there are unsaved changes */
    const hasUnsavedChanges = React.useCallback((): boolean => {
        return (saveQueue.current?.hasPending() ?? false) ||
            (saveQueue.current?.getQueueLength() ?? 0) > 0 ||
            (saveQueue.current?.isProcessingQueue() ?? false) ||
            activeSaveCountRef.current > 0;
    }, [saveQueue]);

    // Cleanup timer on unmount
    React.useEffect(() => {
        return () => {
            if (savedTimerRef.current) {
                clearTimeout(savedTimerRef.current);
            }
        };
    }, []);

    return {
        saveStatus,
        setSaveStatus,
        saveType,
        setSaveType,
        markSaveStarted,
        markSaveCompleted,
        markSaveCompletedWithStaleView,
        markSaveFailed,
        hasUnsavedChanges
    };
}